import React, { useState, forwardRef, useImperativeHandle, useCallback } from "react";
import { Splitter, SplitterOnChangeEvent, SplitterPane, SplitterPaneProps } from "@progress/kendo-react-layout";
import SelectedAssetMetricsReadDto from "../../../../Models/Dashboard/SelectedAssetMetricsReadDto";
import TreeComponent from "../Components/Analysis/TreeComponent";
import AnalysisTileComponent, { AnalysisTileComponentRef } from "../Components/Analysis/AnalysisTileComponent";

interface Props {
  asset: SelectedAssetMetricsReadDto;
  dateFilter: { startDate: Date, endDate: Date };
  setDateFilter: (filter: { startDate: Date, endDate: Date }) => void;
}

export interface AssetAnalysisTabRef {
  refresh: () => Promise<void>;
}

const AssetAnalysisTab = forwardRef<AssetAnalysisTabRef, Props>(({ asset, dateFilter, setDateFilter }, ref) => {
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [treeSelectionState, setTreeSelectionState] = useState<{ [id: string]: boolean }>({});
  const tileRef = React.useRef<AnalysisTileComponentRef>(null);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      if (tileRef.current) {
        await tileRef.current.refresh();
      }
    }
  }));

  const [panes, setPanes] = useState<SplitterPaneProps[]>([
    { size: '20%', min: '20px', collapsible: true },
    { min: '20px', collapsible: false }
  ]);

  const onChange = (event: SplitterOnChangeEvent) => {
    setPanes(event.newState);
  };

  const handleSelectedItemsChange = useCallback((newSelectedItems: any[]) => {
    // If newSelectedItems is already enhanced (has endpoint details), use it directly
    if (newSelectedItems.length > 0 && typeof newSelectedItems[0] === 'object' && newSelectedItems[0].entityType) {
      setSelectedItems(newSelectedItems);
      return;
    }

    // If newSelectedItems is just an array of IDs, enhance them with endpoint types
    if (!asset?.assetConfigurationDetails?.endpoints) {
      setSelectedItems([]);
      return;
    }

    // Transform the selected IDs to include only ID and endpoint type
    const enhancedSelectedItems = newSelectedItems.map(id => {
      const endpoint = asset.assetConfigurationDetails.endpoints.find(ep => ep.id.toString() === id);
      if (endpoint) {
        return {
          id: endpoint.id,
          entityType: endpoint.entityType
        };
      }
      return null;
    }).filter(item => item !== null);

    setSelectedItems(enhancedSelectedItems);
  }, [asset]);

  return (
    <div className="dashboard-analysis-tab-tiles m-2 flex flex-1 min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <Splitter
        panes={panes}
        onChange={onChange}
        style={{ height: '100%' }}
      >
        <SplitterPane orientation="horizontal" overlay={false} containsSplitter={false}>
          <TreeComponent
            setSelectedItems={handleSelectedItemsChange}
            asset={asset}
            selectionState={treeSelectionState}
            setSelectionState={setTreeSelectionState}
          />
        </SplitterPane>
        <SplitterPane orientation="horizontal" overlay={false} containsSplitter={false}>
          <AnalysisTileComponent
            ref={tileRef}
            selectedItems={selectedItems}
            asset={asset}
            sharedDateFilter={dateFilter}
          />
        </SplitterPane>
      </Splitter>
    </div>
  );
});

export default AssetAnalysisTab;